import { gql, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import { corporateGroupTableConfigs, useVariables } from '../../../variables'
import { IApiDataType } from '../../types'

interface IQueryDataType {
  getCustomizedReport: {
    nodes: {
      reportResult: {
        tableData: (
          | {
              locationId: number
              locationInfo: {
                name: string
              }
            }
          | {
              locationGroupId: number
              name: string
            }
        )[]
      }
    }[]
  }
}

const query = gql`
  query smgBreakdownByStore($iReportName: String!, $iInputParams: JSON!) {
    getCustomizedReport(
      iCustomizedReportName: $iReportName
      iInputParams: $iInputParams
    ) {
      nodes {
        reportName
        reportDefinition
        reportResult
      }
    }
  }
`

export const fwWingstopSmgBreakdownByStoreConfigs = {
  ...corporateGroupTableConfigs,
  cpkScore: 'number',
  qscCount: 'number',
  qscScore: 'number',
  digitalCount: 'number',
  wingyouCount: 'number',
  wingyouScore: 'percent',
  deliveryCount: 'number',
  digitalPercent: 'percent',
  deliveryPercent: 'percent',
  inaccurateCount: 'number',
  nonDigitalCount: 'number',
  inaccuratePercent: 'percent',
  nonDigitalPercent: 'percent',
  dissatisfactionCount: 'number',
  dissatisfactionPercent: 'percent',
} as const

const useSmgBreakdownByStore = () => {
  const { variables } = useVariables()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const dateFilter = useDateFilter()
  const locationGroupId = groupFilter?.ids?.[0]
  const { data } = useQuery<IQueryDataType>(query, {
    variables: {
      iReportName: hasGroupBy
        ? 'LIST_LOCATION_SMG_TABLE'
        : 'LIST_LOCATION_GROUP_SMG_TABLE',
      iInputParams: {
        ...(hasGroupBy
          ? {
              locationGroupId,
            }
          : {
              locationGroupType: groupFilter?.parentId,
            }),
        startDate: dateFilter.startDate,
        endDate: dateFilter.endDate,
      },
    },
    skip: !dateFilter.startDate || !dateFilter.endDate || !locationGroupId,
  })

  return {
    data: useMemo((): IApiDataType => {
      if (!data) return null

      const variablesData = variables.corporateGroup
      const locations =
        variablesData && 'locations' in variablesData
          ? variablesData.locations
          : undefined
      const locationGroups =
        variablesData && 'locationGroups' in variablesData
          ? variablesData.locationGroups
          : undefined

      return data.getCustomizedReport.nodes[0]?.reportResult.tableData.map(
        (n) => {
          const id = (() => {
            if ('locationId' in n) return n.locationId
            return n.locationGroupId
          })()
          const tableRow = (locations || locationGroups)?.[id]?.tableRow || {
            [hasGroupBy ? 'location' : 'locationGroup']: {
              id,
              label: 'locationInfo' in n ? n.locationInfo.name : n.name,
            },
          }

          return {
            ...tableRow,
            ...n,
            id: id.toString(),
            parentId: 'root',
          }
        },
      )
    }, [data, variables, hasGroupBy]),
    loading: false,
  }
}

export default useSmgBreakdownByStore
